import React, { FC, useState } from 'react';
import { Popover, FlexGroup, Icon, Text, Select, Tooltip, useTectonTheme, FlexItem } from '@lithos';
import { getSideNavigationWorkspaces } from '../../redesign/util';
import { useUserSettings } from '../../context/UserSettingsContext';
import { AvailableWorkspaces, Workspace } from '../ComponentRedesign/SharedComponentTypes';
import UrlUtils from '../../../utils/url-utils';
import { useNavigate } from 'react-router-dom';
import { SelectGroupProps } from '@lithos/SelectGroup';
import styled from '@emotion/styled';
import WorkspaceIcon from '../WorkpsaceIcon';
import WorkspaceUtils from '../../../utils/workspace-utils';

const FlexGroupWrapper = styled.div<{ isOpen: boolean }>`
  cursor: pointer;
  border-radius: ${({ theme }) => theme.v1.size['2']}px;
  background-color: ${({ isOpen, theme }) =>
    isOpen ? theme.v1.colors.background.hover : theme.v1.colors.background.default};
  margin: ${({ theme }) => theme.v1.size['1']}px 0px;
  margin-bottom: ${({ theme }) => theme.v1.size['3']}px;
  overflow-x: hidden;

  &:hover {
    background-color: ${({ theme }) => theme.v1.colors.background.hover};
  }

  .trigger-expanded {
    display: block;
  }
  .trigger-compressed {
    display: none;
  }

  // Large Side Nav
  @container (max-width: ${({ theme }) => theme.v1.navigationBreakpoints.s}px) {
    .trigger-expanded {
      display: none;
    }
    .trigger-compressed {
      display: block;
    }
  }
`;

const TooltipWrapper = styled.div<{ active: boolean }>`
  cursor: pointer;

  width: ${({ theme }) => theme.v1.size['8']}px;
  height: ${({ theme }) => theme.v1.size['8']}px;

  background-color: ${({ theme, active }) =>
    active ? theme.v1.colors.background.empty : theme.v1.colors.background.default};
  border-radius: ${({ theme }) => theme.v1.size['2']}px;

  .color-icon {
    display: ${({ active }) => {
      return active ? 'block' : 'none';
    }};
  }

  .mono-icon {
    display: ${({ active }) => {
      return active ? 'none' : 'block';
    }};
  }

  &:hover {
    background-color: ${({ theme, active }) =>
      !active ? theme.v1.colors.background.hover : theme.v1.colors.background.empty};
    .color-icon {
      display: block;
    }

    .mono-icon {
      display: none;
    }
  }
`;

const WorkspaceName = styled.div`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const WorkspaceSelector: FC = () => {
  const { theme } = useTectonTheme();

  const navigate = useNavigate();
  const { allWorkspaces, workspace, canShowMainApplication, isLoadingWorkspaces } = useUserSettings();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const workspaces: AvailableWorkspaces = getSideNavigationWorkspaces(allWorkspaces ?? []);

  const onWorkspaceSelected = (selectedWorkspace: Workspace) => {
    const workspaceURLPath = UrlUtils.getRedirectWorkspaceUrl(workspace ?? '', selectedWorkspace?.name);
    navigate(workspaceURLPath);
    setIsOpen(false);
  };

  const groups: SelectGroupProps[] = [
    {
      title: `Recent (${workspaces.recent.length})`,
      items: workspaces.recent.map((workspace) => ({
        avatar: {
          square: true,
          type: 'icon',
          icon: workspace.workspaceType === 'Live' ? 'ProductionWorkspace' : 'DevelopmentWorkspace',
          size: 'm',
          color:
            workspace.workspaceType === 'Live'
              ? theme.v1.colors.viz['5-yellow'].text
              : theme.v1.colors.viz['10-green'].text,
          backgroundColor:
            workspace.workspaceType === 'Live'
              ? theme.v1.colors.viz['5-yellow'].behindText
              : theme.v1.colors.viz['10-green'].behindText,
        },
        name: workspace.name,
        onClick: () => {
          onWorkspaceSelected(workspace);
        },
      })),
    },
    {
      title: `Live (${workspaces.live.length})`,
      items: workspaces.live.map((workspace) => ({
        name: workspace.name,
        avatar: {
          square: true,
          type: 'icon',
          icon: 'ProductionWorkspace',
          size: 'm',
          color: theme.v1.colors.viz['5-yellow'].text,
          backgroundColor: theme.v1.colors.viz['5-yellow'].behindText,
        },
        onClick: () => {
          onWorkspaceSelected(workspace);
        },
      })),
    },
    {
      title: `Development (${workspaces.development.length})`,
      items: workspaces.development.map((workspace) => ({
        name: workspace.name,
        avatar: {
          square: true,
          type: 'icon',
          icon: 'DevelopmentWorkspace',
          size: 'm',
          color: theme.v1.colors.viz['10-green'].text,
          backgroundColor: theme.v1.colors.viz['10-green'].behindText,
        },
        onClick: () => {
          onWorkspaceSelected(workspace);
        },
      })),
    },
  ];

  // Find workspace in live/development arrays, fallback to default if current workspace not accessible
  const selectedWorkspace =
    [...workspaces.live, ...workspaces.development].find((ws) => ws.name === workspace) ||
    [...workspaces.live, ...workspaces.development].find((ws) => ws.name === WorkspaceUtils.getDefaultWorkspace());

  if (!selectedWorkspace) {
    return <></>;
  }

  return (
    <Popover
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      trigger={
        <>
          <FlexGroupWrapper isOpen={isOpen}>
            <div className="trigger-expanded">
              <FlexGroup justifyContent="space-between" alignItems="center" padding="0">
                <FlexItem grow="1" shrink="1">
                  <FlexGroup alignItems="center" padding="1" gapSize="1">
                    <FlexItem grow="0" shrink="0">
                      <WorkspaceIcon workspaceType={selectedWorkspace?.workspaceType ?? 'Live'} />
                    </FlexItem>
                    <FlexItem grow="1" shrink="1">
                      <WorkspaceName>{selectedWorkspace?.name}</WorkspaceName>
                    </FlexItem>
                  </FlexGroup>
                </FlexItem>
                <FlexItem grow="0" shrink="0">
                  <Icon icon="ChevronRight" type="mono" size="m" />
                </FlexItem>
              </FlexGroup>
            </div>
            <div className="trigger-compressed">
              <TooltipWrapper active={isOpen} className="item-with-tooltip">
                <FlexGroup alignItems="center" padding="0" justifyContent="center">
                  <Tooltip
                    offsetSize={12}
                    trigger={
                      <span style={{ cursor: 'pointer' }}>
                        <Icon
                          icon={
                            (selectedWorkspace?.workspaceType ?? 'Live') === 'Live'
                              ? 'ProductionWorkspace'
                              : 'DevelopmentWorkspace'
                          }
                          size="s"
                          type="mono"
                        />
                      </span>
                    }
                    content={selectedWorkspace?.name}
                    placement="right"
                  />
                </FlexGroup>
              </TooltipWrapper>
            </div>
          </FlexGroupWrapper>
        </>
      }
      body={<Select groups={groups} filterable={true} />}
    />
  );
};

export default WorkspaceSelector;
